import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { loadVocabularyCatalog, loadConsolidatedVocabularyTopic } from '../../services/vocabularyV2';
import { loadConversationCategories, loadConversationChapter, loadGrammarPractice } from '../../services/practiceV2';
import { StreakService } from '../../services/StreakService';
import { XpService } from '../../services/XpService';
import { AchievementsService } from '../../services/AchievementsService';
import { SrsService } from '../../services/SrsService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { SoundEffectService } from '../../services/SoundEffectService';
import { ExerciseScoreService } from '../../services/ExerciseScoreService';
import { ChunkyButton, GameProgressBar, GameSegmentBar, Confetti, StarRow } from '../../components/GameUI';
import { REVIEW_TRIGGER_AT, PLAY_STORE_URL } from '../../config/constants';
import { dataImagesUrl } from '../../services/dataApi';

const REVIEW_COUNT_KEY = 'practice_sessions_count';
const REVIEW_ASKED_KEY = 'practice_review_asked';
const REVIEW_TRIGGER   = REVIEW_TRIGGER_AT;

async function handleSessionComplete(score, total, langPair) {
  const perfect = score === total;
  if (navigator.vibrate) navigator.vibrate(perfect ? [50, 30, 80] : [30]);

  await StreakService.recordActivity();

  const xp = await XpService.addXp(score * 2);
  if (xp.goalJustReached) AchievementsService.showBadgeToast(await AchievementsService.tryUnlock('xp_goal_hit'));
  if (xp.totalXp >= 100)  AchievementsService.showBadgeToast(await AchievementsService.tryUnlock('xp_100'));
  if (xp.totalXp >= 1000) AchievementsService.showBadgeToast(await AchievementsService.tryUnlock('xp_1000'));

  AchievementsService.showBadgeToast(await AchievementsService.tryUnlock('first_session'));
  if (perfect) {
    SoundEffectService.playCelebration();
    AchievementsService.showBadgeToast(await AchievementsService.tryUnlock('perfect_quiz'));
  }

  const streak = StreakService.getStreak();
  if (streak >= 3)  AchievementsService.showBadgeToast(await AchievementsService.tryUnlock('streak_3'));
  if (streak >= 7)  AchievementsService.showBadgeToast(await AchievementsService.tryUnlock('streak_7'));
  if (streak >= 30) AchievementsService.showBadgeToast(await AchievementsService.tryUnlock('streak_30'));

  const count = parseInt(localStorage.getItem(REVIEW_COUNT_KEY) || '0', 10) + 1;
  localStorage.setItem(REVIEW_COUNT_KEY, String(count));
  return count; // caller uses this to decide whether to show the review prompt
}

/* ── helpers ── */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function vocabImageUrl(category, filename) {
  if (!filename) return null;
  return dataImagesUrl(`vocabulary/${category}/${filename}`);
}

function entryText(entry) {
  if (entry && typeof entry === 'object' && !Array.isArray(entry)) return entry.text == null ? '—' : String(entry.text);
  if (Array.isArray(entry)) return entry[0] == null ? '—' : String(entry[0]);
  if (entry == null) return '—';
  return String(entry);
}

function entryImage(entry) {
  if (entry && typeof entry === 'object' && !Array.isArray(entry)) return entry.image || entry.imageName || '';
  if (!Array.isArray(entry)) return '';
  return entry[1] ? String(entry[1]) : '';
}

function entryEmoji(entry) {
  return entry && typeof entry === 'object' && !Array.isArray(entry) ? String(entry.emoji || '') : '';
}

/* ── category color palette (cycled by server-provided index) ── */
const CAT_PALETTE = [
  { gradient: 'from-amber-400 to-orange-500',   shadow: 'shadow-amber-400/30'   },
  { gradient: 'from-pink-400 to-pink-600',       shadow: 'shadow-pink-400/30'    },
  { gradient: 'from-emerald-400 to-teal-500',   shadow: 'shadow-emerald-400/30' },
  { gradient: 'from-orange-400 to-orange-600',  shadow: 'shadow-orange-400/30'  },
  { gradient: 'from-violet-400 to-violet-600',  shadow: 'shadow-violet-400/30'  },
  { gradient: 'from-blue-400 to-blue-600',      shadow: 'shadow-blue-400/30'    },
  { gradient: 'from-yellow-400 to-amber-600',   shadow: 'shadow-yellow-400/30'  },
  { gradient: 'from-green-400 to-green-600',    shadow: 'shadow-green-400/30'   },
  { gradient: 'from-red-400 to-red-600',        shadow: 'shadow-red-400/30'     },
  { gradient: 'from-rose-300 to-red-500',       shadow: 'shadow-rose-300/30'    },
  { gradient: 'from-teal-400 to-emerald-600',   shadow: 'shadow-teal-400/30'    },
  { gradient: 'from-indigo-400 to-violet-500',  shadow: 'shadow-indigo-400/30'  },
];

/* ── categories hook (fetched from the V2 vocabulary catalogs) ── */
function useVocabIndex(homeLang) {
  const { t } = useTranslation();
  const [data, setData] = useState({ thematic: [], wordclass: [], grammar: [] });
  useEffect(() => {
    let active = true;
    loadVocabularyCatalog(homeLang)
      .then(raw => {
        if (!active) return;
        const enrich = (arr, offset = 0) =>
          (arr ?? []).map((cat, i) => ({ ...cat, ...CAT_PALETTE[(i + offset) % CAT_PALETTE.length] }));
        setData({
          thematic:  enrich(raw.thematic,  0),
          wordclass: enrich(raw.wordclass, 3),
          grammar:   enrich([{ id: 'tenses', label: t('practice.verbTensesCategory'), icon: '📅' }], 7),
        });
      })
      .catch(() => {});
    return () => { active = false; };
  }, [t, homeLang]);
  return data;
}

function useSentenceCats(targetCode) {
  const [cats, setCats] = useState([]);

  useEffect(() => {
    if (!targetCode) return undefined;
    let active = true;
    loadConversationCategories(targetCode, targetCode)
      .then(raw => {
        if (!active) return;
        const enriched = (raw ?? [])
          .slice()
          .sort((a, b) => {
            const levels = ['simple', 'medium', 'hard', 'very_hard'];
            const levelDifference = levels.indexOf(a.level) - levels.indexOf(b.level);
            return levelDifference || Number(a.levelOrder) - Number(b.levelOrder);
          })
          .map((chapter, i) => ({
            id:    chapter.id,
            label: chapter.label,
            title: chapter.title ?? '',
            icon:  '💬',
            ...CAT_PALETTE[(i + 5) % CAT_PALETTE.length],
          }));
        if (enriched.length > 0) setCats(enriched);
      })
      .catch(() => { if (active) setCats([]); });
    return () => { active = false; };
  }, [targetCode]);

  return cats;
}

/* ── grammar data hook ── */
function useGrammarData(targetCode, categoryId) {
  const [tenses, setTenses]   = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!targetCode || !categoryId) return undefined;
    let active = true;
    setLoading(true);
    setTenses([]);
    loadGrammarPractice(targetCode)
      .then(data => { if (active) { setTenses(data); setLoading(false); } })
      .catch(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [targetCode, categoryId]);
  return { tenses, loading };
}

function useFlashcards(nativeCode, targetCode, category, type = 'thematic') {
  const [native, setNative]   = useState(null);
  const [target, setTarget]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!nativeCode || !targetCode || !category || type === 'grammar') return undefined;
    let active = true;
    setLoading(true);
    setNative(null);
    setTarget(null);
    setError(null);

    if (type === 'sentences') {
      Promise.allSettled([
        loadConversationChapter(targetCode, nativeCode, category),
        loadConversationChapter(targetCode, targetCode, category),
      ]).then(([rN, rT]) => {
        if (!active) return;
        if (rN.status === 'fulfilled') setNative(rN.value);
        if (rT.status === 'fulfilled') setTarget(rT.value);
        if (rN.status === 'rejected' && rT.status === 'rejected') setError(true);
        setLoading(false);
      });
      return () => { active = false; };
    }

    // homeLang is always targetCode (the curriculum home); native/target
    // codes are resolved relative to it in one call (see vocabularyV2.js).
    loadConsolidatedVocabularyTopic(type, targetCode, nativeCode, targetCode, category)
      .then((pack) => {
        if (!active) return;
        setNative(pack.reader);
        setTarget(pack.target);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });
    return () => { active = false; };
  }, [nativeCode, targetCode, category, type]);

  return { native, target, loading, error };
}

/* ── shared image ── */
function VocabImage({ src, alt, className }) {
  const [err, setErr] = useState(false);
  if (!src || err) return null;
  return <img src={src} alt={alt} onError={() => setErr(true)} className={className} />;
}

/* ── flashcard ── */
function Flashcard({ nativeItem, targetItem, nativeLang, targetLang, cardIndex, total, category, onShuffle }) {
  const { t } = useTranslation();
  const [flipped, setFlipped] = useState(false);
  useEffect(() => setFlipped(false), [nativeItem, targetItem]);

  const nativeText = entryText(nativeItem);
  const targetText = entryText(targetItem);
  const imgSrc     = vocabImageUrl(category, entryImage(nativeItem) || entryImage(targetItem));
  const emoji      = entryEmoji(nativeItem) || entryEmoji(targetItem);

  return (
    <div className="flex flex-col gap-5">
      {/* counter */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-black uppercase tracking-widest text-slate-400">
          {t('practice.cardCount', { current: cardIndex + 1, total })}
        </span>
        <button
          onClick={onShuffle}
          className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-500 shadow-sm hover:border-indigo-300 hover:text-indigo-600 transition"
        >
          🔀 {t('practice.shuffle')}
        </button>
      </div>

      {/* 3-D flip card */}
      <div
        className="cursor-pointer select-none"
        onClick={() => setFlipped(f => !f)}
        style={{ perspective: '1200px' }}
      >
        <div
          style={{
            position: 'relative',
            height: imgSrc ? '320px' : '260px',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.55s cubic-bezier(0.4,0,0.2,1)',
            transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          {/* front */}
          <div
            style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-3xl bg-gradient-to-br from-indigo-500 to-violet-600 p-8 shadow-2xl shadow-indigo-400/25"
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-white/50">
              {nativeLang?.label}
            </p>
            {imgSrc && (
              <VocabImage
                src={imgSrc} alt={nativeText}
                className="h-28 w-28 rounded-2xl object-contain bg-white/15 p-2 shadow-lg"
              />
            )}
            {!imgSrc && emoji && <span className="text-7xl" aria-hidden="true">{emoji}</span>}
            <p className="text-5xl font-black text-white text-center leading-tight">{nativeText}</p>
            <p className="text-[11px] text-white/30 font-semibold tracking-wide">{t('practice.tapToReveal')}</p>
          </div>

          {/* back */}
          <div
            style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-3xl bg-gradient-to-br from-rose-500 to-pink-600 p-8 shadow-2xl shadow-rose-400/25"
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-white/50">
              {targetLang?.label}
            </p>
            {imgSrc && (
              <VocabImage
                src={imgSrc} alt={targetText}
                className="h-28 w-28 rounded-2xl object-contain bg-white/15 p-2 shadow-lg"
              />
            )}
            {!imgSrc && emoji && <span className="text-7xl" aria-hidden="true">{emoji}</span>}
            <p dir={targetLang?.dir || 'ltr'} className="text-5xl font-black text-white text-center leading-tight">{targetText}</p>
            <p className="text-[11px] text-white/30 font-semibold tracking-wide">{t('practice.tapToFlipBack')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── shared chunky nav button (prev/next, used by flashcards + grammar reference) ── */
function NavButton({ label, enabled, onClick }) {
  return (
    <ChunkyButton
      color="#FFFFFF"
      shadowColor="#CBD5E1"
      onClick={onClick}
      disabled={!enabled}
      className="flex-1 py-3.5 text-center"
      style={{ color: enabled ? '#334155' : '#CBD5E1' }}
    >
      {label}
    </ChunkyButton>
  );
}

/* ── shared chunky quiz option tile (used by QuizCard + GrammarSection quiz) ── */
function QuizOptionTile({ label, isCorrect, isPicked, revealed, onClick }) {
  let bg = '#F8FAFC', border = '#E2E8F0', fg = '#334155';
  if (revealed) {
    if (isCorrect) { bg = '#D7FFB8'; border = '#58CC02'; fg = '#2B6B00'; }
    else if (isPicked) { bg = '#FFDFE0'; border = '#FF4B4B'; fg = '#8B0000'; }
    else { bg = '#FFFFFF'; border = '#F1F5F9'; fg = '#CBD5E1'; }
  }
  return (
    <button
      type="button"
      onClick={revealed ? undefined : onClick}
      disabled={revealed}
      className="flex items-center justify-between rounded-2xl px-4 py-3.5 text-left text-sm font-black transition-all"
      style={{ background: bg, border: `2.5px solid ${border}`, color: fg, cursor: revealed ? 'default' : 'pointer' }}
    >
      <span className="truncate">{label}</span>
      {revealed && (isCorrect || isPicked) && (
        <span
          className="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs text-white"
          style={{ background: isCorrect ? '#58CC02' : '#FF4B4B' }}
        >
          {isCorrect ? '✓' : '✕'}
        </span>
      )}
    </button>
  );
}

/* ── quiz ── */
function QuizCard({ cards, nativeLang, targetLang, category, langPair, reviewMode = false, onComplete }) {
  const { t } = useTranslation();
  const [index, setIndex]           = useState(0);
  const [chosen, setChosen]         = useState(null);
  const [, setScore]                = useState(0);
  const [done, setDone]             = useState(false);
  const [showReview, setShowReview] = useState(false);
  const scoreRef                    = useRef(0);

  const questions = useMemo(() => cards.map((card, i) => {
    const wrong = shuffle(cards.filter((_, j) => j !== i).map(c => c.targetText)).slice(0, 3);
    return { ...card, options: shuffle([card.targetText, ...wrong]) };
  }), [cards]);

  const q      = questions[index];
  const imgSrc = vocabImageUrl(q?.category || category, q?.imgFilename);

  function pick(opt) {
    if (chosen) return;
    const correct = opt === q.targetText;
    LearningProgressService.record(langPair || 'guest', 'vocabulary', 1, {
      activityId: `word:${q.key}`,
    });
    if (navigator.vibrate) navigator.vibrate(correct ? 50 : [50, 50, 50]);
    correct ? SoundEffectService.playCorrect() : SoundEffectService.playWrong();
    if (langPair) {
      SrsService.review({
        wordKey: q.key,
        nativeWord: q.nativeText,
        targetWord: q.targetText,
        imageName: q.imgFilename || null,
        category: q.category || category,
        langPair,
      }, correct ? 5 : 1).catch(() => {});
    }
    if (correct) { setScore(s => { scoreRef.current = s + 1; return s + 1; }); }
    setChosen(opt);
  }

  function next() {
    if (index + 1 >= questions.length) {
      setDone(true);
      handleSessionComplete(scoreRef.current, questions.length, langPair).then((sessionCount) => {
        const alreadyAsked = localStorage.getItem(REVIEW_ASKED_KEY);
        if (!alreadyAsked && sessionCount >= REVIEW_TRIGGER) setShowReview(true);
        onComplete?.();
      });
      return;
    }
    setIndex(i => i + 1);
    setChosen(null);
  }

  if (done) {
    const pct = Math.round((scoreRef.current / questions.length) * 100);
    const perfect = scoreRef.current === questions.length;
    const stars = ExerciseScoreService.starsForPercent(pct);
    return (
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative bg-gradient-to-br from-indigo-500 to-violet-600 px-6 py-12 text-center">
          {perfect && <Confetti />}
          <p className="text-6xl mb-4">
            {perfect ? '🏆' : scoreRef.current > questions.length / 2 ? '🎉' : '💪'}
          </p>
          <h3 className="text-2xl font-black text-white">{t('practice.quizComplete')}</h3>
          <p className="mt-1 text-white/60 text-sm">
            {t('practice.correctScore', { score: scoreRef.current, total: questions.length })}
          </p>
          <div className="mt-3 flex justify-center">
            <StarRow count={stars} size={26} />
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="mb-2 flex justify-between text-xs font-bold text-slate-400">
              <span>{t('practice.score')}</span><span className="text-indigo-600 font-black">{pct}%</span>
            </div>
            <GameProgressBar value={pct / 100} color="#4F46E5" height={10} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
              <p className="text-xl font-black text-indigo-600">+{scoreRef.current * 2}</p>
              <p className="mt-0.5 text-[10px] font-black uppercase tracking-wider text-slate-400">XP</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
              <p className={`text-xl font-black ${questions.length - scoreRef.current === 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                {questions.length - scoreRef.current}
              </p>
              <p className="mt-0.5 text-[10px] font-black uppercase tracking-wider text-slate-400">{t('practice.weakWords')}</p>
            </div>
          </div>

          {/* Review prompt — shown after REVIEW_TRIGGER sessions */}
          {showReview && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center">
              <p className="text-lg mb-1">{t('practice.ratePrompt')}</p>
              <p className="text-xs text-slate-500 mb-3">{t('practice.rateDesc')}</p>
              <div className="flex gap-2 justify-center">
                <a
                  href={PLAY_STORE_URL}
                  target="_blank" rel="noreferrer"
                  onClick={() => { localStorage.setItem(REVIEW_ASKED_KEY, '1'); setShowReview(false); }}
                  className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-black text-white hover:bg-amber-600 transition"
                >
                  {t('practice.ratePlayStore')}
                </a>
                <button
                  onClick={() => { localStorage.setItem(REVIEW_ASKED_KEY, '1'); setShowReview(false); }}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 transition"
                >
                  {t('practice.maybeLater')}
                </button>
              </div>
            </div>
          )}

          <ChunkyButton
            color="#4F46E5"
            onClick={() => { setIndex(0); setChosen(null); setScore(0); scoreRef.current = 0; setDone(false); setShowReview(false); }}
            className="w-full py-3.5 text-center text-white"
          >
            {reviewMode ? t('practice.reviewAgain') : t('practice.tryAgain')}
          </ChunkyButton>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      {/* progress strip */}
      <div className="px-5 pt-4">
        <GameSegmentBar total={questions.length} current={index} />
      </div>

      {/* question */}
      <div className="bg-gradient-to-br from-indigo-50 to-violet-50 px-6 py-8 text-center">
        {imgSrc && (
          <div className="flex justify-center mb-5">
            <VocabImage
              src={imgSrc} alt={q.nativeText}
              className="h-32 w-32 rounded-2xl object-contain bg-white border border-indigo-100 p-2 shadow-sm"
            />
          </div>
        )}
        {!imgSrc && q?.emoji && <div className="mb-5 text-7xl" aria-hidden="true">{q.emoji}</div>}
        <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400 mb-2">
          {nativeLang?.label}
        </p>
        <p className="text-4xl font-black text-slate-900">{q.nativeText}</p>
        <p className="mt-2 text-xs text-slate-400">
          {t('practice.translateTo', { lang: targetLang?.label })}
        </p>
      </div>

      {/* options */}
      <div dir={targetLang?.dir || 'ltr'} className="grid grid-cols-2 gap-2.5 p-5">
        {q.options.map((opt) => (
          <QuizOptionTile
            key={opt}
            label={opt}
            isCorrect={opt === q.targetText}
            isPicked={opt === chosen}
            revealed={!!chosen}
            onClick={() => pick(opt)}
          />
        ))}
      </div>

      {chosen && (
        <div className="px-5 pb-5">
          <ChunkyButton
            color="#58CC02"
            shadowColor="#4CAD02"
            onClick={next}
            className="w-full py-3.5 text-center text-white"
          >
            {index + 1 >= questions.length ? t('practice.seeResults') : t('common.next')}
          </ChunkyButton>
        </div>
      )}
    </div>
  );
}

/* ── pronunciation scoring ── */
function speechTokens(value) {
  const normalized = value.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();
  if (!normalized) return [];
  return normalized.includes(' ') ? normalized.split(' ') : Array.from(normalized);
}

function editDistance(left, right) {
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  left.forEach((leftToken, leftIndex) => {
    const current = [leftIndex + 1];
    right.forEach((rightToken, rightIndex) => {
      current.push(Math.min(
        current[rightIndex] + 1,
        previous[rightIndex + 1] + 1,
        previous[rightIndex] + (leftToken === rightToken ? 0 : 1),
      ));
    });
    previous = current;
  });
  return previous[previous.length - 1];
}

function speechScoreBreakdown(expected, heard) {
  const expectedTokens = speechTokens(expected);
  const heardTokens = speechTokens(heard);
  const positionMatches = expectedTokens.filter((token, index) => heardTokens[index] === token).length;
  const accuracy = expectedTokens.length ? Math.round((positionMatches / expectedTokens.length) * 100) : 0;
  const maxLength = Math.max(expectedTokens.length, heardTokens.length);
  const pronunciation = maxLength ? Math.max(0, Math.round((1 - editDistance(expectedTokens, heardTokens) / maxLength) * 100)) : 0;
  const completion = expectedTokens.length ? Math.min(100, Math.round((heardTokens.length / expectedTokens.length) * 100)) : 0;
  const fluency = heardTokens.length ? Math.max(0, 100 - Math.round((Math.abs(heardTokens.length - expectedTokens.length) / Math.max(1, expectedTokens.length)) * 100)) : 0;
  const overall = Math.round((accuracy + pronunciation + fluency + completion) / 4);
  return { accuracy, pronunciation, fluency, completion, overall };
}

/* ── SpeakCard ── */
const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;

export function SpeakCard({ cards, targetLang, category, onComplete = null }) {
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const [idx, setIdx]               = useState(0);
  const [recState, setRecState]     = useState('idle'); // idle | recording | result
  const [transcript, setTranscript] = useState('');
  const [score, setScore]           = useState(null);
  const [scoreDetails, setScoreDetails] = useState(null);
  const recognitionRef              = useRef(null);
  const transcriptRef               = useRef('');
  const mountedRef                  = useRef(true);

  const card   = cards[idx] ?? null;
  const imgSrc = card ? vocabImageUrl(category, card.imgFilename) : null;
  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
    : `en_${targetLang?.code || 'en'}`;

  function finishResult(final) {
    const details = speechScoreBreakdown(card.targetText, final);
    setScore(details.overall);
    setScoreDetails(details);
    details.overall >= 70 ? SoundEffectService.playCorrect() : SoundEffectService.playWrong();
    const snapshot = LearningProgressService.record(langPair, 'speak', 1, {
      activityId: `word:${card.key || card.targetText}`,
    });
    AchievementsService.tryUnlock('first_speech').then(AchievementsService.showBadgeToast);
    if ((snapshot.lifetime.speak || 0) >= 100) {
      AchievementsService.tryUnlock('spoken_100').then(AchievementsService.showBadgeToast);
    }
    if (details.overall < 70) {
      SrsService.review({
        wordKey: `speak_${category}_${card.key || card.targetText}`,
        nativeWord: card.nativeText,
        targetWord: card.targetText,
        imageName: card.imgFilename || null,
        category: `speaking:${category}`,
        langPair,
        easiness: 2.5,
        interval: 1,
        repetitions: 0,
        nextReview: new Date().toISOString(),
      }, 2);
    }
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recognitionRef.current?.abort();
    };
  }, []);

  function startRecording() {
    if (!SpeechRecognitionAPI) return;
    const r = new SpeechRecognitionAPI();
    r.lang = targetLang?.tts || targetLang?.code || 'en-US';
    r.interimResults = true;
    r.continuous = true;
    transcriptRef.current = '';

    r.onresult = (e) => {
      const text = Array.from(e.results).map(res => res[0].transcript).join('');
      transcriptRef.current = text;
      if (!mountedRef.current) return;
      setTranscript(text);
    };

    r.onend = () => {
      const final = transcriptRef.current.trim();
      if (!mountedRef.current) return;
      setTranscript(final || '(no speech detected)');
      finishResult(final);
      setRecState('result');
    };

    r.onerror = (e) => {
      if (!mountedRef.current) return;
      setTranscript(e.error === 'no-speech' ? '(no speech detected)' : `Error: ${e.error}`);
      setScore(0);
      setScoreDetails({ accuracy: 0, pronunciation: 0, fluency: 0, completion: 0, overall: 0 });
      setRecState('result');
    };

    recognitionRef.current = r;
    r.start();
    setRecState('recording');
    setTranscript('');
    setScore(null);
  }

  function stopRecording() {
    recognitionRef.current?.stop();
  }

  function playReference() {
    if (!card?.targetText || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(card.targetText);
    utterance.lang = targetLang?.tts || targetLang?.code || 'en-US';
    utterance.rate = 0.85;
    window.speechSynthesis.speak(utterance);
  }

  function retry() { setTranscript(''); setScore(null); setScoreDetails(null); setRecState('idle'); }
  function next()  {
    if (idx >= cards.length - 1) {
      onComplete?.();
      return;
    }
    setIdx(i => Math.min(cards.length - 1, i + 1));
    setTranscript(''); setScore(null); setScoreDetails(null); setRecState('idle');
  }

  if (!card) return null;
  const speechSupported = !!SpeechRecognitionAPI;

  const pct   = score ?? 0;
  const good  = pct >= 80;
  const ok    = pct >= 50;
  const emoji = good ? '🎉' : ok ? '👍' : '💪';
  const col   = good ? 'text-emerald-600' : ok ? 'text-amber-600' : 'text-red-500';
  const bg    = good ? 'bg-emerald-50 border-emerald-300' : ok ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300';

  return (
    <div className="space-y-4">
      {!speechSupported && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center">
          <p className="font-black text-amber-800">⚠️ {t('practice.speechNotSupported')}</p>
          <p className="mt-1 text-sm text-amber-600">{t('practice.speechChromeEdge')}</p>
        </div>
      )}
      {/* counter */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-black uppercase tracking-widest text-slate-400">
          {t('practice.wordCount', { current: idx + 1, total: cards.length })}
        </span>
      </div>
      <GameProgressBar value={cards.length ? (idx + 1) / cards.length : 0} color="#7C3AED" height={8} />

      {/* word card */}
      <div className="rounded-3xl bg-gradient-to-br from-violet-500 to-purple-700 p-8 text-center shadow-2xl shadow-violet-400/25 select-none">
        <p className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-3">
          {t('practice.sayThisIn', { lang: targetLang?.label })}
        </p>
        {imgSrc && (
          <div className="flex justify-center mb-4">
            <VocabImage src={imgSrc} alt={card.targetText}
              className="h-24 w-24 rounded-2xl object-contain bg-white/15 p-2 shadow-lg" />
          </div>
        )}
        {!imgSrc && card?.emoji && <div className="mb-5 text-7xl" aria-hidden="true">{card.emoji}</div>}
        <p dir={targetLang?.dir || 'ltr'} className="text-5xl font-black text-white mb-2">{card.targetText}</p>
        <p className="text-sm text-white/40 italic">{card.nativeText}</p>
        <button
          type="button"
          onClick={playReference}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-black text-white transition hover:bg-white/25"
        >
          🔊 {t('practice.listenSample', { defaultValue: 'Listen and repeat' })}
        </button>
      </div>

      {/* live transcript while recording */}
      {recState === 'recording' && transcript && (
        <div dir={targetLang?.dir || 'ltr'} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm italic text-slate-600">
          "{transcript}"
        </div>
      )}

      {/* result block */}
      {recState === 'result' && (
        <div className={`rounded-2xl border-2 p-5 ${bg}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-3xl font-black ${col}`}>{emoji} {pct}%</span>
            <span className={`text-sm font-bold ${col}`}>
              {good ? t('practice.excellent') : ok ? t('practice.goodTry') : t('practice.keepGoing')}
            </span>
          </div>
          <p className="text-[10px] uppercase tracking-wider font-black text-slate-400 mb-0.5">{t('practice.youSaid')}</p>
          <p dir={targetLang?.dir || 'ltr'} className="text-sm font-semibold text-slate-700 italic mb-3">"{transcript}"</p>
          <p className="text-[10px] uppercase tracking-wider font-black text-slate-400 mb-0.5">{t('practice.expected')}</p>
          <p dir={targetLang?.dir || 'ltr'} className="text-sm font-semibold text-slate-700">"{card.targetText}"</p>
          {scoreDetails && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                [t('practice.scoreWordAccuracy'), scoreDetails.accuracy],
                [t('practice.scorePronunciation'), scoreDetails.pronunciation],
                [t('practice.scoreFluency'), scoreDetails.fluency],
                [t('practice.scoreCompletion'), scoreDetails.completion],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-white/70 p-2 text-center">
                  <p className="text-lg font-black text-slate-800">{value}%</p>
                  <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* action buttons */}
      {recState === 'idle' && speechSupported && (
        <ChunkyButton color="#7C3AED" shadowColor="#6D28D9" onClick={startRecording} className="w-full py-4 text-white">
          <span className="flex items-center justify-center gap-2"><span className="text-lg">🎤</span> {t('practice.record')}</span>
        </ChunkyButton>
      )}
      {recState === 'idle' && !speechSupported && (
        <ChunkyButton color="#7C3AED" shadowColor="#6D28D9" onClick={next} className="w-full py-4 text-white">
          {idx >= cards.length - 1 ? t('common.done') : t('common.next')}
        </ChunkyButton>
      )}
      {recState === 'recording' && (
        <ChunkyButton color="#FF4B4B" shadowColor="#D93A3A" onClick={stopRecording} className="w-full animate-pulse py-4 text-white">
          <span className="flex items-center justify-center gap-2"><span className="text-lg">⏹</span> {t('practice.stopRecording')}</span>
        </ChunkyButton>
      )}
      {recState === 'result' && (
        <div className="flex gap-3">
          <ChunkyButton color="#FFFFFF" shadowColor="#CBD5E1" onClick={retry} className="flex-1 py-3.5 text-slate-700">
            {t('practice.retryRecord')}
          </ChunkyButton>
          <ChunkyButton
            color="#7C3AED"
            shadowColor="#6D28D9"
            onClick={next}
            disabled={idx >= cards.length - 1 && !onComplete}
            className="flex-1 py-3.5 text-white"
          >
            {idx >= cards.length - 1 ? t('common.done') : t('common.next')}
          </ChunkyButton>
        </div>
      )}
    </div>
  );
}

function DueReviewPrompt({ count, loading, onStart }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 rounded-3xl border border-indigo-200 bg-white p-4 shadow-sm">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-xl">
        🧠
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black text-slate-900">
          {t('practice.wordsDue', { count })}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">{t('practice.reviewWeakWords')}</p>
      </div>
      <ChunkyButton
        color="#4F46E5"
        onClick={onStart}
        disabled={loading}
        depth={4}
        className="px-4 py-2.5 text-xs text-white"
      >
        {loading ? t('common.loading') : t('practice.review')}
      </ChunkyButton>
    </div>
  );
}

/* ── GrammarSection ── */
function GrammarSection({ tenses, loading, targetLang, mode }) {
  const { t } = useTranslation();
  const [tenseIdx, setTenseIdx] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [qIdx, setQIdx]       = useState(0);
  const [chosen, setChosen]   = useState(null);
  const [score, setScore]     = useState(0);
  const [done, setDone]       = useState(false);

  function buildQuestions(ts) {
    const allForms = [...new Set(ts.flatMap(t => t.rows.map(r => r.form)))];
    const qs = ts.flatMap(tense =>
      tense.rows.map(row => {
        const wrongs = shuffle(allForms.filter(f => f !== row.form)).slice(0, 3);
        return { tenseName: tense.name, person: row.person, correct: row.form,
                 options: shuffle([row.form, ...wrongs]) };
      })
    );
    return shuffle(qs);
  }

  useEffect(() => {
    if (tenses.length > 0) {
      setQuestions(buildQuestions(tenses));
      setQIdx(0); setChosen(null); setScore(0); setDone(false); setTenseIdx(0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenses]);

  if (loading) return (
    <div className="flex flex-col items-center gap-3 rounded-3xl border border-slate-200 bg-white py-20 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
        <div className="h-6 w-6 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
      </div>
      <p className="text-sm font-bold text-slate-500">{t('grammar.loading')}</p>
    </div>
  );
  if (!tenses.length) return (
    <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-slate-300 bg-white py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
        <p className="text-3xl">📭</p>
      </div>
      <p className="font-black text-slate-700">{t('grammar.noData', { lang: targetLang?.label })}</p>
    </div>
  );

  /* ── Reference ── */
  if (mode === 'reference') {
    const tense = tenses[tenseIdx];
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-black uppercase tracking-widest text-slate-400">
            {t('practice.tenseCount', { current: tenseIdx + 1, total: tenses.length })}
          </span>
          <span className="text-xs font-bold text-violet-600">{targetLang?.nativeLabel || targetLang?.label}</span>
        </div>
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="bg-gradient-to-br from-indigo-500 to-violet-600 p-6">
            <h3 className="text-lg font-black text-white">{tense.name}</h3>
            <p className="mt-1 text-sm text-white/60">{tense.use}</p>
            <span className="mt-2 inline-block rounded-xl bg-white/15 px-3 py-1 text-xs italic text-white/70">
              e.g. {tense.verb}
            </span>
          </div>
          <div className="divide-y divide-slate-100 px-5 py-3">
            {tense.rows.map(row => (
              <div key={row.person} className="flex items-center gap-4 py-2.5">
                <span className="w-28 text-sm font-semibold text-slate-500">{row.person}</span>
                <span dir={targetLang?.dir || 'ltr'} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-black text-indigo-600">
                  {row.form}
                </span>
              </div>
            ))}
          </div>
          {tense.examples?.length > 0 && (
            <div className="border-t border-slate-100 px-5 pb-5 pt-3">
              <p className="mb-2 text-[10px] font-black uppercase tracking-wider text-slate-400">{t('grammar.examples')}</p>
              {tense.examples.map((ex, i) => (
                <p key={i} dir={targetLang?.dir || 'ltr'} className="text-sm italic text-slate-600">• {ex}</p>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <NavButton label={t('common.prev')} enabled={tenseIdx > 0} onClick={() => setTenseIdx(i => Math.max(0, i - 1))} />
          <NavButton label={t('common.next')} enabled={tenseIdx < tenses.length - 1} onClick={() => setTenseIdx(i => Math.min(tenses.length - 1, i + 1))} />
        </div>
      </div>
    );
  }

  /* ── Quiz ── */
  if (done) {
    const pct = Math.round((score / questions.length) * 100);
    const perfect = score === questions.length;
    const stars = ExerciseScoreService.starsForPercent(pct);
    return (
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="relative bg-gradient-to-br from-indigo-500 to-violet-600 px-6 py-12 text-center">
          {perfect && <Confetti />}
          <p className="text-6xl mb-4">{perfect ? '🏆' : score > questions.length / 2 ? '🎉' : '💪'}</p>
          <h3 className="text-2xl font-black text-white">{t('grammar.quizDone')}</h3>
          <p className="mt-1 text-white/60 text-sm">{t('practice.correctScore', { score, total: questions.length })}</p>
          <div className="mt-3 flex justify-center">
            <StarRow count={stars} size={26} />
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="mb-2 flex justify-between text-xs font-bold text-slate-400">
              <span>{t('practice.score')}</span><span className="text-indigo-600 font-black">{pct}%</span>
            </div>
            <GameProgressBar value={pct / 100} color="#4F46E5" height={10} />
          </div>
          <ChunkyButton
            color="#4F46E5"
            onClick={() => { setQuestions(buildQuestions(tenses)); setQIdx(0); setChosen(null); setScore(0); setDone(false); }}
            className="w-full py-3.5 text-center text-white"
          >
            {t('practice.tryAgain')}
          </ChunkyButton>
        </div>
      </div>
    );
  }

  const q = questions[qIdx];
  if (!q) return null;
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="px-5 pt-4">
        <GameSegmentBar total={questions.length} current={qIdx} />
      </div>
      <div className="bg-gradient-to-br from-indigo-50 to-violet-50 px-6 py-8 text-center">
        <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400 mb-3">{q.tenseName}</p>
        <p className="text-4xl font-black text-slate-900">
          {q.person} <span className="text-slate-300 font-light">→ ?</span>
        </p>
        <p className="mt-2 text-xs text-slate-400">{targetLang?.label}</p>
      </div>
      <div dir={targetLang?.dir || 'ltr'} className="grid grid-cols-2 gap-2.5 p-5">
        {q.options.map(opt => (
          <QuizOptionTile
            key={opt}
            label={opt}
            isCorrect={opt === q.correct}
            isPicked={opt === chosen}
            revealed={!!chosen}
            onClick={() => {
              if (navigator.vibrate) navigator.vibrate(opt === q.correct ? 50 : [50, 50, 50]);
              opt === q.correct ? SoundEffectService.playCorrect() : SoundEffectService.playWrong();
              setChosen(opt);
              if (opt === q.correct) setScore(s => s + 1);
            }}
          />
        ))}
      </div>
      {chosen && (
        <div className="px-5 pb-5">
          <ChunkyButton
            color="#58CC02"
            shadowColor="#4CAD02"
            onClick={() => {
              if (qIdx + 1 >= questions.length) setDone(true);
              else { setQIdx(i => i + 1); setChosen(null); }
            }}
            className="w-full py-3.5 text-center text-white"
          >
            {qIdx + 1 >= questions.length ? t('practice.seeResults') : t('common.next')}
          </ChunkyButton>
        </div>
      )}
    </div>
  );
}

/* ── page ── */
export default function PracticePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, languagePrefs } = useAuth();
  const { languages } = useLanguages();

  const [practiceType, setPracticeType] = useState('thematic'); // thematic|wordclass|sentences|grammar
  const [mode, setMode]                 = useState(() =>
    new URLSearchParams(window.location.search).get('mode') === 'speak'
      ? 'speak'
      : 'flashcard'
  );
  const [category, setCategory]         = useState('');
  const [cardIndex, setCardIndex]       = useState(0);
  const [dueCount, setDueCount]         = useState(0);
  const [reviewCards, setReviewCards]   = useState([]);
  const [reviewMode, setReviewMode]     = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  const nativeCode = languagePrefs?.nativeLanguage;
  const targetCode = languagePrefs?.targetLanguage;
  const vocabIndex = useVocabIndex(targetCode);
  const sentenceCats = useSentenceCats(targetCode);

  const categories = useMemo(() => ({
    thematic:  vocabIndex.thematic,
    wordclass: vocabIndex.wordclass,
    sentences: sentenceCats,
    grammar:   vocabIndex.grammar,
  }[practiceType] ?? []), [practiceType, vocabIndex, sentenceCats]);

  // Set initial category once the list loads
  useEffect(() => {
    if (categories.length > 0 && !category) setCategory(categories[0].id);
  }, [categories, category]);

  const langMap    = Object.fromEntries(languages.map(l => [l.code, l]));
  const nativeLang = nativeCode ? langMap[nativeCode] : null;
  const targetLang = targetCode ? langMap[targetCode] : null;
  const langPair   = nativeCode && targetCode ? `${nativeCode}_${targetCode}` : '';

  const { native, target, loading, error } = useFlashcards(nativeCode, targetCode, category || null, practiceType);
  const { tenses: grammarTenses, loading: grammarLoading } = useGrammarData(
    practiceType === 'grammar' ? targetCode : null,
    practiceType === 'grammar' ? category   : null,
  );

  const baseCards = useMemo(() => {
    const keys = Array.from(new Set([
      ...Object.keys(native?.content ?? {}),
      ...Object.keys(target?.content ?? {}),
    ]));

    return keys
      .map(k => ({
        key: k,
        nativeText:  entryText(native?.content?.[k]),
        targetText:  entryText(target?.content?.[k]),
        imgFilename: entryImage(native?.content?.[k]) || entryImage(target?.content?.[k]),
        emoji:       entryEmoji(native?.content?.[k]) || entryEmoji(target?.content?.[k]),
        category,
      }))
      .filter(c => c.nativeText !== '—' && c.targetText !== '—');
  }, [native, target, category]);

  const [cards, setCards] = useState([]);

  // Shuffle whenever category data arrives
  useEffect(() => {
    if (baseCards.length === 0) return;
    setCards(shuffle(baseCards));
    setCardIndex(0);
    setReviewMode(false);
  }, [baseCards]);

  const refreshDueCount = () => {
    if (!langPair) return;
    setDueCount(SrsService.getDueCount(langPair));
  };

  useEffect(() => {
    refreshDueCount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langPair]);

  useEffect(() => {
    if (!isAuthenticated || !langPair) return;
    LearningProgressService.record(langPair, 'practice', 1, {
      activityId: 'started:practice',
    });
  }, [isAuthenticated, langPair]);

  function startDueReview() {
    if (!langPair) return;
    setReviewLoading(true);
    const due = SrsService.getDueCards(langPair).map(card => ({
      key: card.wordKey,
      nativeText: card.nativeWord,
      targetText: card.targetWord,
      imgFilename: card.imageName || '',
      category: card.category || category,
    }));
    setReviewCards(due);
    setDueCount(due.length);
    setReviewMode(due.length > 0);
    setPracticeType('thematic');
    setMode('quiz');
    setCardIndex(0);
    setReviewLoading(false);
  }

  function reshuffle() {
    setCards(shuffle(baseCards));
    setCardIndex(0);
  }

  // Supports the Home page's "Today's Lesson" due-review CTA (/practice?review=1).
  const reviewParamHandled = useRef(false);
  useEffect(() => {
    if (reviewParamHandled.current || !langPair) return;
    if (new URLSearchParams(window.location.search).get('review') === '1') {
      reviewParamHandled.current = true;
      startDueReview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langPair]);

  const activeCat = categories.find(c => c.id === category) ?? categories[0] ?? null;
  const cardsForPractice = reviewMode ? reviewCards : cards;
  const currentCard = cardsForPractice[cardIndex] ?? null;

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 20% 0%, #e0e7ff 0%, #f8fafc 55%, #fdf4ff 100%)' }}>
      <div className="mx-auto max-w-lg px-4 py-10 space-y-6">

        {/* header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-black text-slate-900">{t('practice.title')}</h1>
            {nativeLang && targetLang && (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                <span className="font-semibold text-slate-700">{nativeLang.label}</span>
                <svg className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                <span className="font-semibold text-slate-700">{targetLang.label}</span>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate('/')}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm hover:bg-slate-50 transition"
          >
            ← {t('nav.home')}
          </button>
        </div>

        {/* practice type selector */}
        <div className="flex gap-2 overflow-x-auto pb-0.5 -mx-4 px-4 scrollbar-none">
          {[
            { id: 'thematic',  label: t('practice.thematic'),  icon: '📚' },
            { id: 'wordclass', label: t('practice.wordClass'), icon: '🔤' },
            { id: 'sentences', label: t('practice.sentences'), icon: '💬' },
            { id: 'grammar',   label: t('practice.grammar'),   icon: '📝' },
          ].map(pt => {
            const active = practiceType === pt.id;
            return (
              <button key={pt.id}
                onClick={() => {
                  if (practiceType === pt.id) return;
                  const newCats = {
                    thematic:  vocabIndex.thematic,
                    wordclass: vocabIndex.wordclass,
                    sentences: sentenceCats,
                    grammar:   vocabIndex.grammar,
                  }[pt.id] ?? [];
                  setPracticeType(pt.id);
                  setCategory(newCats[0]?.id ?? '');
                  setCardIndex(0);
                  setReviewMode(false);
                  setMode(pt.id === 'grammar' ? 'reference' : 'flashcard');
                }}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-xs font-black transition-all ${
                  active
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-400/30'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300 shadow-sm'
                }`}
              >
                <span>{pt.icon}</span>
                <span>{pt.label}</span>
              </button>
            );
          })}
        </div>

        {/* category selector */}
        <div className="flex gap-2 overflow-x-auto pb-0.5 -mx-4 px-4 scrollbar-none">
          {categories.length === 0
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 h-10 w-24 rounded-2xl bg-slate-200 animate-pulse" />
              ))
            : categories.map(cat => {
                const active = category === cat.id;
                return (
                  <button
                    key={cat.id}
                    title={cat.title || cat.label}
                    onClick={() => { setCategory(cat.id); setCardIndex(0); setReviewMode(false); setMode('flashcard'); }}
                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all ${
                      active
                        ? `bg-gradient-to-r ${cat.gradient} text-white shadow-lg ${cat.shadow}`
                        : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300 shadow-sm'
                    }`}
                  >
                    <span className="text-base leading-none">{cat.icon}</span>
                    <span>{cat.label}</span>
                  </button>
                );
              })
          }
        </div>

        {/* mode toggle */}
        <div className="flex gap-1.5 rounded-2xl bg-slate-100/80 p-1.5 border border-slate-200">
          {(practiceType === 'grammar'
            ? [{ id: 'reference', icon: '📖', label: t('practice.reference') }, { id: 'quiz', icon: '🧠', label: t('practice.quizMode') }]
            : practiceType === 'sentences'
              ? [{ id: 'flashcard', icon: '🃏', label: t('practice.flashcard') }, { id: 'quiz', icon: '🧠', label: t('practice.quizMode') }]
              : [{ id: 'flashcard', icon: '🃏', label: t('practice.flashcard') }, { id: 'quiz', icon: '🧠', label: t('practice.quizMode') }, { id: 'speak', icon: '🎤', label: t('practice.speak') }]
          ).map(m => (
            <button
              key={m.id}
              onClick={() => { setMode(m.id); setCardIndex(0); if (m.id !== 'quiz') setReviewMode(false); }}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-black transition-all ${
                mode === m.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <span>{m.icon}</span>
              <span>{m.label}</span>
            </button>
          ))}
        </div>

        {(dueCount > 0 || reviewLoading) && (
          <DueReviewPrompt
            count={dueCount}
            loading={reviewLoading}
            onStart={startDueReview}
          />
        )}

        {/* content */}
        {practiceType === 'grammar' ? (
          <GrammarSection
            key={`grammar-${category}-${targetCode}`}
            tenses={grammarTenses}
            loading={grammarLoading}
            targetLang={targetLang}
            mode={mode}
          />
        ) : loading && !reviewMode ? (
          <div className="flex flex-col items-center gap-3 rounded-3xl border border-slate-200 bg-white py-16 text-center shadow-sm">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
            </div>
            <p className="text-sm font-bold text-slate-500">{t('practice.loading', { cat: activeCat?.label })}</p>
          </div>

        ) : error || cardsForPractice.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50">
              <p className="text-3xl">📭</p>
            </div>
            <p className="font-black text-slate-700">{t('practice.noWordsYet')}</p>
            <p className="text-sm text-slate-400 max-w-[220px]">
              {t('practice.catNotAvailable', { cat: activeCat?.label, lang: targetLang?.label })}
            </p>
          </div>

        ) : mode === 'speak' ? (
          <SpeakCard
            key={`${category}-speak`}
            cards={cardsForPractice}
            targetLang={targetLang}
            category={category}
          />

        ) : mode === 'flashcard' && currentCard ? (
          <div className="space-y-4">
            <Flashcard
              nativeItem={native?.content?.[currentCard.key]}
              targetItem={target?.content?.[currentCard.key]}
              nativeLang={nativeLang}
              targetLang={targetLang}
              cardIndex={cardIndex}
              total={cardsForPractice.length}
              category={category}
              onShuffle={reshuffle}
            />
            <GameProgressBar
              value={cardsForPractice.length ? (cardIndex + 1) / cardsForPractice.length : 0}
              color="#4F46E5"
              height={8}
            />
            <div className="flex gap-3">
              <NavButton label={t('common.prev')} enabled={cardIndex > 0} onClick={() => setCardIndex(i => Math.max(0, i - 1))} />
              <NavButton
                label={t('common.next')}
                enabled={cardIndex < cardsForPractice.length - 1}
                onClick={() => setCardIndex(i => Math.min(cardsForPractice.length - 1, i + 1))}
              />
            </div>
          </div>

        ) : (
          <QuizCard
            key={`${reviewMode ? 'review' : category}-quiz-${cardsForPractice.length}`}
            cards={cardsForPractice}
            nativeLang={nativeLang}
            targetLang={targetLang}
            category={category}
            langPair={langPair}
            reviewMode={reviewMode}
            onComplete={refreshDueCount}
          />
        )}

      </div>
    </div>
  );
}
